<!--
 * @Author: Nemo
 * @Description: 响应式弹窗组件 - 移动端实现
 * @Date: 2024-05-29
-->
<template>
  <div v-if="visible || closing" class="responsive-dialog">
    <!-- 弹窗蒙层 -->
    <div class="responsive-dialog-mask" @click="handleMaskClick" :class="{ 'mask-fade-out': closing }" />

    <!-- 移动端底部弹窗 -->
    <div class="responsive-dialog-container mobile-popup" :class="{ 'slide-down': closing }">
      <div class="popup-header" v-if="displayTitle">
        <div class="popup-title-wrapper">
          <img v-if="type" :src="typeIcon" class="payment-icon" :class="{'apple-pay-icon': type === 'apple', 'google-pay-icon': type === 'google'}" alt="" />
          <div class="popup-title">{{ displayTitle }}</div>
        </div>
        <div class="popup-close" @click="handleClose">
          <i class="el-icon-close"></i>
        </div>
      </div>
      <div class="popup-content">
        <slot>
          <!-- 默认插槽 -->
          <billing-form
            v-if="showBillingForm"
            ref="billingForm"
            :fields="actualBillingFields"
            :order-info="orderInfo"
            @validate="handleBillingFormValidate"
            @field-focus="handleFieldFocus"
            @field-change="handleFieldChange"
          />
        </slot>
      </div>
      <div class="popup-footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
      <div class="popup-footer" v-else-if="showDefaultFooter">
        <button class="popup-btn popup-confirm-btn" @click="handleConfirm">
          <div style="display: flex; align-items: center; justify-content: center;">
            <img v-if="type === 'google'" src="./image/icon_google.png" alt="Google Pay" class="btn-icon" />
            <img v-if="type === 'apple'" src="./image/icon_apple.png" alt="Apple Pay" class="btn-icon" />
            {{ $t('message.checkout.pay') }}
            <template v-if="orderInfo && orderInfo.txnAmount && orderInfo.txnCurrency">
              {{ formatMoneyWithCommas(orderInfo.txnAmount) }} {{ orderInfo.txnCurrency }}
            </template>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatMoneyWithCommas } from '@/utils/tools/format';

import BillingForm from './billing-form.vue';
import ApplePayIcon from './image/icon_apple_pay.svg';
import GooglePayIcon from './image/icon_google_pay.png';
export default {
  name: 'responsive-dialog-mobile',
  components: {
    BillingForm,
  },
  data() {
    return {
      closing: false,
    };
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
      validator: function(value) {
        return ['', 'apple', 'google'].includes(value);
      },
    },
    closeOnClickMask: {
      type: Boolean,
      default: true,
    },
    showDefaultFooter: {
      type: Boolean,
      default: true,
    },
    confirmText: {
      type: String,
      default: '确认',
    },
    cancelText: {
      type: String,
      default: '取消',
    },
    billingFields: {
      type: Array,
      default: () => [],
    },
    showBillingForm: {
      type: Boolean,
      default: false,
    },
    orderInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    // 获取最终使用的账单字段
    actualBillingFields() {
      return this.billingFields;
    },

    displayTitle() {
      if (this.type === 'apple') {
        return 'Apple Pay';
      } else if (this.type === 'google') {
        return 'Google Pay';
      }
      return this.title;
    },
    typeIcon() {
      if (this.type === 'apple') {
        return ApplePayIcon;
      } else if (this.type === 'google') {
        return GooglePayIcon;
      }
      return '';
    },
  },
  created() {
    // 不再初始化默认账单字段
  },
  mounted() {
    // 弹窗出现时禁止背景滚动
    if (this.visible) {
      document.body.style.overflow = 'hidden';
    }
  },
  watch: {
    visible(val) {
      // 当弹窗显示或隐藏时，控制背景滚动
      if (val) {
        document.body.style.overflow = 'hidden';
      } else {
        // 关闭动画结束后才恢复滚动
        if (!this.closing) {
          document.body.style.overflow = '';
        }
      }
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    formatMoneyWithCommas,
    // 移除默认字段生成方法

    handleClose() {
      // 开始关闭动画
      this.closing = true;

      // 动画结束后再真正关闭
      setTimeout(() => {
        this.closing = false;
        this.$emit('update:visible', false);
        this.$emit('close');
        document.body.style.overflow = '';
      }, 300); // 动画时长，与CSS中的duration一致
    },
    handleMaskClick() {
      if (this.closeOnClickMask) {
        this.handleClose();
      }
    },
    handleConfirm() {
      if (this.showBillingForm && this.$refs.billingForm) {
        // 使用billing-form组件的validate方法验证表单
        this.$refs.billingForm.validate()
          .then(formData => {
            // 验证通过，发送成功事件
            this.$emit('confirm', formData);
          })
          .catch(error => {
            console.log('error：', error);
            // 自动滚动到错误位置
            this.$nextTick(() => {
              const errorDom = document.querySelector('.popup-content .is-error');
              if (errorDom) {
                errorDom.scrollIntoView({
                  block: 'center',
                  behavior: 'smooth',
                });
              }
            });
          });
      } else {
        this.$emit('confirm');
      }
    },
    handleCancel() {
      this.$emit('cancel');
      this.handleClose();
    },
    handleFieldFocus(fieldName) {
      this.$emit('field-focus', fieldName);
    },
    handleFieldChange(fieldData) {
      this.$emit('field-change', fieldData);
    },
    handleBillingFormValidate(isValid, formData) {
      if (isValid) {
        this.$emit('confirm', formData);
      } else {
        // 自动滚动到错误位置
        this.$nextTick(() => {
          const errorDom = document.querySelector('.popup-content .is-error');
          if (errorDom) {
            errorDom.scrollIntoView({
              block: 'center',
              behavior: 'smooth',
            });
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ===== 共用样式 ===== */
.responsive-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  backdrop-filter: blur(4px);
  transition: opacity 0.3s ease;

  &.mask-fade-out {
    opacity: 0;
  }
}

.responsive-dialog-container {
  position: fixed;
  z-index: 2001;
  background: #FFFFFF;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

/* ===== 移动端样式 ===== */
.mobile-popup {
  left: 0;
  bottom: 0;
  width: 100%;
  height: 39.25rem;
  border-radius: 20px 20px 0 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  padding: 24px 8px;
  animation: slide-up 0.3s ease;

  &.slide-down {
    transform: translateY(100%);
  }
}

.mobile-popup .popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.mobile-popup .popup-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0;
}

.mobile-popup .payment-icon {
  height: 32px;
  width: auto;
  margin-right: 16px;
}

.mobile-popup .popup-title {
  font-weight: 600;
  font-size: 16px;
  color: #1A202C;
}

.mobile-popup .popup-close {
  cursor: pointer;
  color: #8D98AF;
}

.mobile-popup .popup-content {
  flex: 1;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  margin: 16px 0;
  border-radius: 8px;
  border: 1px solid #EDF1F7;
  padding: 24px 8px;
}

.mobile-popup .popup-footer {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: #FFFFFF;
  padding-bottom: 16px;
}

.mobile-popup .popup-btn {
  width: 100%;
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}

.mobile-popup .popup-confirm-btn {
  background: #000000;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-popup .popup-cancel-btn {
  background: #F0F3F6;
  color: #182058;
}

.mobile-popup .popup-default-btn {
  background: #FFFFFF;
  color: #182058;
  border: 1px solid #D0D5DD;
}

/* ===== 动画 ===== */
@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* ===== 特定图标样式 ===== */
.apple-pay-icon {
  width: 50px !important;
}

.google-pay-icon {
  width: 60.16px !important;
}

.btn-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}
</style>
